/**
 * 应用路由配置
 * 定义应用的所有路由、权限、菜单等信息
 * 纯配置数据，不包含 React 组件
 */
import React from 'react';
import {
  FileTextOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  BarChartOutlined,
  HomeOutlined
} from '@ant-design/icons';

// React Router 未来配置
export const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// 路由配置对象
export const routerConfig = {
  futureConfig: routerFutureConfig
};

// 路由配置接口
export interface RouteConfigItem {
  path: string;
  requiredPermissions: string[];
  component: string; // 组件名称
  icon: string; // 图标名称
  label: string; // 菜单标签
  breadcrumb: string; // 面包屑标题
  hideInMenu?: boolean; // 是否在菜单中隐藏
}

// 图标映射
const ICON_MAP = {
  HomeOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  BarChartOutlined
} as const;

/**
 * 路由配置数据
 * 纯配置，不包含 React 元素
 */
export const ROUTE_CONFIG: RouteConfigItem[] = [
  {
    path: '/',
    requiredPermissions: [],
    component: 'Navigate', // 特殊标记，表示重定向
    icon: 'HomeOutlined',
    label: '首页',
    breadcrumb: '首页',
    hideInMenu: true
  },
  {
    path: '/list',
    requiredPermissions: ['msspace:space:view'],
    component: 'SpaceList',
    icon: 'FileTextOutlined',
    label: '空间列表',
    breadcrumb: '空间列表'
  },
  {
    path: '/tree',
    requiredPermissions: ['msspace:space:view'],
    component: 'SpaceTree',
    icon: 'ApartmentOutlined',
    label: '空间结构',
    breadcrumb: '空间结构'
  },
  {
    path: '/tags',
    requiredPermissions: ['msspace:tags:view'],
    component: 'TagManagement',
    icon: 'TagsOutlined',
    label: '标签管理',
    breadcrumb: '标签管理'
  },
  {
    path: '/visual',
    requiredPermissions: ['msspace:visual:view'],
    component: 'SpaceVisual',
    icon: 'DashboardOutlined',
    label: '空间可视化',
    breadcrumb: '空间可视化'
  }
];

/**
 * 路由配置类型定义
 */
export interface RouteConfig {
  path: string;
  element: React.ReactElement;
}

/**
 * 页面权限映射
 * 用于快速查找页面所需的权限
 * 直接使用硬编码的三段式权限
 */
export const PAGE_PERMISSIONS = {
  '/list': ['msspace:space:view'],
  '/tree': ['msspace:space:view'],
  '/tags': ['msspace:tags:view'],
  '/visual': ['msspace:visual:view']
} as const;

/**
 * 操作权限映射
 * 用于检查具体操作的权限
 */
export const OPERATION_PERMISSIONS = {
  // 空间管理权限
  SPACE_VIEW: 'msspace:space:view',
  SPACE_CREATE: 'msspace:space:create',
  SPACE_UPDATE: 'msspace:space:update',
  SPACE_DELETE: 'msspace:space:delete',

  // 空间列表权限
  LIST_VIEW: 'msspace:space:view',
  LIST_CREATE: 'msspace:space:create',
  LIST_UPDATE: 'msspace:space:update',
  LIST_DELETE: 'msspace:space:delete',

  // 空间树权限
  TREE_VIEW: 'msspace:space:view',
  TREE_CREATE: 'msspace:space:create',
  TREE_UPDATE: 'msspace:space:update',
  TREE_DELETE: 'msspace:space:delete',

  // 标签管理权限
  TAG_VIEW: 'msspace:tag:view',
  TAG_CREATE: 'msspace:tag:create',
  TAG_UPDATE: 'msspace:tag:update',
  TAG_DELETE: 'msspace:tag:delete',

  // 空间可视化权限
  VISUAL_VIEW: 'msspace:visual:view',
  VISUAL_CREATE: 'msspace:visual:create',
  VISUAL_UPDATE: 'msspace:visual:update',
  VISUAL_DELETE: 'msspace:visual:delete'
} as const;

/**
 * 获取页面所需权限
 * @param path 页面路径
 * @returns 权限数组
 */
export const getPagePermissions = (path: string): string[] => {
  const permissions = PAGE_PERMISSIONS[path as keyof typeof PAGE_PERMISSIONS];
  return permissions ? [...permissions] : [];
};

/**
 * 检查页面是否需要权限验证
 * @param path 页面路径
 * @returns 是否需要权限验证
 */
export const requiresAuth = (path: string): boolean => {
  return path !== '/' && Object.keys(PAGE_PERMISSIONS).includes(path);
};

/**
 * 权限检查工具函数
 */
export const PermissionUtils = {
  /**
   * 检查用户是否有指定权限
   * @param userPermissions 用户权限列表
   * @param requiredPermissions 所需权限列表
   * @returns 是否有权限
   */
  hasPermission: (userPermissions: string[], requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  },

  /**
   * 检查用户是否有任一权限
   * @param userPermissions 用户权限列表
   * @param requiredPermissions 所需权限列表
   * @returns 是否有任一权限
   */
  hasAnyPermission: (userPermissions: string[], requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  },

  /**
   * 检查用户是否有所有权限
   * @param userPermissions 用户权限列表
   * @param requiredPermissions 所需权限列表
   * @returns 是否有所有权限
   */
  hasAllPermissions: (userPermissions: string[], requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => userPermissions.includes(permission));
  },

  /**
   * 获取缺失的权限
   * @param userPermissions 用户权限列表
   * @param requiredPermissions 所需权限列表
   * @returns 缺失的权限列表
   */
  getMissingPermissions: (userPermissions: string[], requiredPermissions: string[]): string[] => {
    return requiredPermissions.filter(permission => !userPermissions.includes(permission));
  }
};


/**
 * 根据路径获取路由配置
 * @param path 页面路径
 * @returns 路由配置项
 */
export const getRouteConfig = (path: string): RouteConfigItem | undefined => {
  return ROUTE_CONFIG.find(route => route.path === path);
};

/**
 * 获取面包屑配置
 * @param path 当前路径
 * @returns 面包屑配置数组
 */
export const getBreadcrumbConfig = (path: string): Array<{ label: string, path?: string }> => {
  const route = getRouteConfig(path);
  if (!route) return [];

  return [
    { label: '首页', path: '/' },
    { label: route.breadcrumb }
  ];
};

/**
 * 从路由配置生成菜单项
 * @param navigate 导航函数
 * @returns 菜单项数组
 */
export const getMenuItems = (navigate: (path: string) => void) => {
  return ROUTE_CONFIG
    .filter((route) => !route.hideInMenu) // 过滤掉隐藏的菜单项
    .map((route) => {
      const IconComponent = ICON_MAP[route.icon as keyof typeof ICON_MAP];
      return {
        key: route.path.replace('/', '') || 'home',
        icon: IconComponent
          ? React.createElement(IconComponent as any)
          : null,
        label: route.label,
        path: route.path,
        onClick: () => navigate(route.path)
      };
    });
};
